import {
  AmountField,
  InvalidInput,
  Popover,
  RangeField,
  readLink,
  type Hotel,
  type RangeInput,
  type RangeValue,
} from '@trippath/shared';
import { useId, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { linkOf } from '../../lib/clock';
import { problemKey } from '../../lib/format';

interface Props {
  /** The trigger's name: what is changed. */
  label: string;
  children: ReactNode;
  currency: string;
  /** A night as it is, when no hotel is in mind. */
  night: RangeInput;
  hotels: Hotel[];
  /** The nights to sleep there: a hotel's price may be said for all of them. */
  nights: number;
  /** The night said, and the hotels in mind, the one stayed at first. */
  onSave: (stay: { night: RangeInput; hotels: Hotel[] }) => Promise<unknown>;
}

interface Draft {
  key: number;
  name: string;
  /** A night there: a hotel has one price. */
  price: number | null;
  link: string;
  /** The link last read, not read again. */
  read?: string;
}

/**
 * A night at a place, pressed: its price, one or a range, or the hotels in mind for it — each
 * with a night's price and its link — the one stayed at giving the night.
 */
export function StayEdit({ label, children, currency, night, hotels, nights, onSave }: Props) {
  const { t } = useTranslation();
  const ids = useId();
  const [open, setOpen] = useState(false);
  const [price, setPrice] = useState<RangeValue>(night);
  const [drafts, setDrafts] = useState<Draft[]>([]);
  const [failed, setFailed] = useState<string | null>(null);
  const [pasted, setPasted] = useState('');
  const [reading, setReading] = useState(false);
  // Each hotel's price for the whole stay rather than a night, as booking sites show it.
  const [whole, setWhole] = useState(false);
  const byWhole = (on: boolean) => {
    setWhole(on);
    setDrafts((was) =>
      was.map((d) =>
        d.price === null
          ? d
          : { ...d, price: on ? d.price * nights : Math.round(d.price / nights) },
      ),
    );
  };

  const start = (opening: boolean) => {
    if (opening) {
      setPrice(night);
      setDrafts(
        hotels.map((h, key) => ({ ...h, key, price: h.price.min, link: h.link ?? '' })),
      );
      setFailed(null);
      setWhole(false);
    }
    setOpen(opening);
  };
  // Each hotel its own key, one past the highest, however many were read at once.
  const add = (hotel: Omit<Draft, 'key'>) => {
    setDrafts((was) => [...was, { ...hotel, key: Math.max(-1, ...was.map((d) => d.key)) + 1 }]);
  };
  // A link pasted is read: the hotel's name, a night's price for the nights it searched, its
  // own address; one that cannot be read is kept as it is, to name by hand.
  const paste = (text: string) => {
    setPasted(text);
    const link = linkOf(text);
    if (typeof link !== 'string') return;
    setReading(true);
    readLink(link)
      .then(
        (read) => {
          add({
            name: read.name ?? '',
            price: read.nightly === null || !whole ? read.nightly : read.nightly * nights,
            link: read.link ?? link,
          });
        },
        () => {
          add({ name: '', price: null, link });
        },
      )
      .finally(() => {
        setPasted('');
        setReading(false);
      });
  };
  const change = (key: number, to: Partial<Draft>) => {
    setDrafts((was) => was.map((d) => (d.key === key ? { ...d, ...to } : d)));
  };
  // A link typed is read once it is left: its name where none is, a night's price, its own
  // address.
  const look = (draft: Draft) => {
    const link = linkOf(draft.link);
    if (typeof link !== 'string' || link === draft.read) return;
    setReading(true);
    readLink(link)
      .then(
        (read) => {
          // Its own address replaces the one typed, and counts as read.
          const own = read.link ?? link;
          change(draft.key, {
            read: own,
            name: draft.name.trim() === '' ? (read.name ?? '') : draft.name,
            link: own,
            ...(read.nightly === null
              ? {}
              : { price: whole ? read.nightly * nights : read.nightly }),
          });
        },
        () => {
          change(draft.key, { read: link });
        },
      )
      .finally(() => {
        setReading(false);
      });
  };
  const range = (value: RangeValue): RangeInput | null =>
    value.min === null || value.max === null || value.min > value.max
      ? null
      : { min: value.min, max: value.max };

  const save = (event: { preventDefault: () => void }) => {
    event.preventDefault();
    const read: Hotel[] = [];
    for (const draft of drafts) {
      const night =
        draft.price === null || !whole ? draft.price : Math.round(draft.price / nights);
      const link = linkOf(draft.link);
      if (draft.name.trim() === '' || night === null || link === undefined) {
        setFailed(t(link === undefined ? 'problem.format' : 'problem.required'));
        return;
      }
      read.push({ name: draft.name.trim(), price: { min: night, max: night }, link });
    }
    const said = range(price);
    if (said === null) {
      setFailed(t('problem.reversed'));
      return;
    }
    setFailed(null);
    onSave({ night: said, hotels: read }).then(
      () => {
        setOpen(false);
      },
      (error: unknown) => {
        const [first] = error instanceof InvalidInput ? error.fields : [];
        setFailed(t(first ? problemKey(first.problem) : 'plan.failed'));
      },
    );
  };

  return (
    <Popover
      open={open}
      onOpenChange={start}
      label={label}
      trigger={(props) => (
        <button {...props} type="button" className="cost-edit" aria-label={label}>
          {children}
        </button>
      )}
    >
      <form className="cost-form" onSubmit={save} noValidate>
        {drafts.length === 0 && (
          <RangeField
            label={t('stopForm.accommodation')}
            value={price}
            currency={currency}
            onChange={setPrice}
          />
        )}
        {drafts.length > 0 && nights > 1 && (
          <label className="check stay-whole">
            <input
              type="checkbox"
              checked={whole}
              onChange={(event) => {
                byWhole(event.target.checked);
              }}
            />
            {t('plan.stayWhole', { count: nights })}
          </label>
        )}
        {drafts.length > 0 && (
          <ul className="stay-hotels" aria-label={t('plan.hotels')}>
            {drafts.map((draft, at) => {
              const link = linkOf(draft.link);
              return (
                <li
                  key={draft.key}
                  className={at === 0 ? 'stay-hotel stay-hotel-chosen' : 'stay-hotel'}
                >
                  <input
                    type="radio"
                    name={`${ids}-stay`}
                    aria-label={t('plan.chooseHotel', { name: draft.name })}
                    checked={at === 0}
                    onChange={() => {
                      // The hotel stayed at goes first: its night is the stay's.
                      setDrafts((was) => [draft, ...was.filter((d) => d.key !== draft.key)]);
                    }}
                  />
                  <input
                    className="text-input"
                    type="text"
                    autoComplete="off"
                    aria-label={t('plan.hotel')}
                    placeholder={t('plan.hotelName')}
                    value={draft.name}
                    onChange={(event) => {
                      change(draft.key, { name: event.target.value });
                    }}
                  />
                  <AmountField
                    // Said again in the other way, whole or a night, and once its link is read.
                    key={`${String(whole)} ${draft.read ?? ''}`}
                    label={whole ? t('plan.stayPrice') : t('stopForm.accommodation')}
                    quiet
                    value={draft.price}
                    currency={currency}
                    onChange={(value) => {
                      change(draft.key, { price: value });
                    }}
                  />
                  <button
                    type="button"
                    className="icon-button icon-button-small"
                    aria-label={t('plan.removeHotel', { name: draft.name })}
                    onClick={() => {
                      setDrafts((was) => was.filter((d) => d.key !== draft.key));
                    }}
                  >
                    ×
                  </button>
                  <div className="stay-hotel-link">
                    <input
                      className="text-input"
                      type="text"
                      autoComplete="off"
                      aria-label={t('plan.link')}
                      placeholder="https://…"
                      value={draft.link}
                      aria-invalid={link === undefined}
                      onChange={(event) => {
                        change(draft.key, { link: event.target.value });
                      }}
                      onBlur={() => {
                        look(draft);
                      }}
                    />
                    {typeof link === 'string' && (
                      <a className="text-link" href={link} target="_blank" rel="noreferrer">
                        {t('plan.openLink')}
                      </a>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
        <div className="stay-add">
          <input
            className="text-input"
            type="text"
            autoComplete="off"
            aria-label={t('plan.pasteHotel')}
            placeholder={t('plan.pasteHotel')}
            value={pasted}
            onChange={(event) => {
              paste(event.target.value);
            }}
          />
          <button
            type="button"
            className="button button-small"
            onClick={() => {
              add({ name: '', price: null, link: '' });
            }}
          >
            {t('plan.addHotel')}
          </button>
        </div>
        {reading && (
          <p className="quiet" role="status">
            {t('plan.readingLink')}
          </p>
        )}
        {failed !== null && (
          <p className="field-error" role="alert">
            {failed}
          </p>
        )}
        <div className="dialog-actions">
          <button
            type="button"
            className="button button-small"
            onClick={() => {
              setOpen(false);
            }}
          >
            {t('dialog.cancel')}
          </button>
          <button type="submit" className="button button-small button-primary">
            {t('car.save')}
          </button>
        </div>
      </form>
    </Popover>
  );
}
